<template>
  <div
    class="relative border rounded-lg bg-white dark:bg-dark-800 text-left w-full"
    :class="isFocused ? 'border-charcoal-950 dark:border-dark-400' : 'border-charcoal-500 dark:border-dark-600'"
  >
    <template v-if="editor && isToolbar">
      <InputPaperLink
        :is-active="isLinkActive"
        :active-link="activeLink"
        @cancel="isLinkActive = false"
        @input="v => {
          editor?.chain().focus().extendMarkRange('link').setLink(v).run()
          activeLink = null
          isLinkActive = false
        }"
      />
      
      <div class="sticky top-2 m-2 h-10 bg-charcoal-100 dark:bg-dark-600 rounded p-1 z-20 flex items-center space-x-1" v-if="styleType !== 'simple'">
        <div
          v-for="command in commands"
          class="size-8 flex items-center justify-center bg-white dark:bg-dark-800 cursor-pointer border border-transparent transition rounded"
          :class="command.isActive !== undefined ? command.isActive : editor.isActive(command.id) ? 'bg-charcoal-950 text-white dark:text-dark-950' : 'hover:bg-charcoal-100 hover:border-charcoal-950 hover:dark:bg-dark-700 hover:dark:border-dark-500'"
          @click="command.onClick"
          :key="command.id"
        >
          <IconBase v-bind="command.icon" />
        </div>
      </div>
    </template>

    <EditorContent
      :class="bodyClass"
      v-if="editor"
      :editor="editor"
    />

    <div class="p-2 bg-charcoal-100 dark:bg-dark-600 rounded-b-lg border-opacity-10 flex items-center justify-end space-x-2" v-if="min || max">
      <div
        class="font-medium rounded-full py-1 pl-1 pr-3 text-2xs pointer-events-none transition flex items-center bg-white dark:bg-dark-900"
        v-if="min"
      >
        <ChartCircle class="mr-1.5" :width="18" :stroke="4" :completion="length / min" /> 
        
        <IconBase name="cute/check-fill" :width="13" v-if="length >= min" />
        <span v-else-if="length > 5">{{ length }}<span v-if="length < min"> / {{ min }}</span></span>
        <span v-else>{{ $t('components.InputPaper.minCharacters', { min }) }}</span>
      </div>

      <div
        class="font-medium rounded-full py-1 pl-1 pr-3 text-2xs pointer-events-none transition flex items-center bg-white dark:bg-dark-900"
        :class="[
          length >= 5 ? '' : 'opacity-0 translate-y-1',
        ]"
        v-if="max"
      >
        <ChartCircle class="mr-1.5" :width="18" :stroke="4" :completion="1 - (length / max)" /> <span v-if="length < max">{{ length }} / {{ max }} max</span><span v-else>{{ $t('components.InputPaper.tooLong') }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ComputedRef } from 'vue'
import { Editor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { NoNewLine } from '@/utils/useTiptap'

const props = withDefaults(defineProps<{
  modelValue: string | undefined
  rows?: number
  min?: number
  max?: number
  bodyClass?: 'text-body' | 'text-post' | 'text-message' | 'text-message-md' | ''
  styleType?: 'base' | 'simple'
  isToolbar?: boolean
  isChat?: boolean
  placeholder?:  string
}>(), {
  rows: 4,
  min: 0,
  max: 0,
  isToolbar: true,
  bodyClass: 'text-body',
  styleType: 'base',
  isChat: false,
  placeholder: ''
})

const emit = defineEmits([ 'update:modelValue', 'focus', 'blur', 'submit' ])

const { modelValue } = toRefs(props)

const editor: Ref<Editor | null> = ref(null)
const isFocused = ref(false)

onMounted(() => {
  editor.value = new Editor({
    content: props.modelValue,
    editorProps: {
      attributes: {
        class: 'text-editor outline-none p-3 pt-1.5',
        style: `min-height: ${(55 * props.rows) - 30}px`
      },
    },
    extensions: [
      StarterKit, Link.configure({
        validate: (url) => {

          return url.includes('http')
        }
      }),
      Placeholder.configure({ placeholder: props.placeholder }),
      ...(props.isChat ? [ NoNewLine.configure({
        action: () => emit('submit')
      }) ] : [])
    ],
    onUpdate ({ editor }) {
      emit('update:modelValue', editor.getHTML(), editor.getText({ blockSeparator: '\n' }))
    },
    onFocus () {
      isFocused.value = true
      emit('focus')
    },
    onBlur () {
      isFocused.value = false
      emit('blur')
    }
  })

  if (editor.value) emit('update:modelValue', editor.value.getHTML(), editor.value.getText({ blockSeparator: '\n' }))
})

watch(modelValue, () => {
  if (!isFocused.value) setContent(modelValue.value)
})

const setContent = (v: string) => {
  if (editor.value) editor.value.chain().setContent(v).run()
}

Link.configure({
  openOnClick: false,
})

const isLinkActive = ref(false)
const activeLink = ref()

interface Command {
  id: string,
  icon: { name: string, width: number },
  isActive?: boolean,
  onClick: () => void
}

const commands: ComputedRef<Command[]> = computed(() => [
  {
    id: 'bold',
    icon: { name: 'cute/bold-fill', width: 14 },
    onClick: () => editor.value ? editor.value.chain().focus().toggleBold().run() : ''
  },
  {
    id: 'italic',
    icon: { name: 'cute/italic-fill', width: 14 },
    onClick: () => editor.value ? editor.value.chain().focus().toggleItalic().run() : ''
  },
  {
    id: 'link',
    icon: { name: 'cute/link-fill', width: 14 },
    onClick: () => {
      activeLink.value = editor.value?.getAttributes('link')
      isLinkActive.value = true
    }
  }
])

const length = computed(() => editor.value?.getText().length || 0)

onBeforeUnmount(() => {
  if (editor.value) editor.value.destroy()
})

onMounted(() => {
  setContent(props.modelValue)
})

defineExpose({
  focus: () => editor.value?.commands.focus(),
  setContent
})
</script>